// Public, indexable SEO landing page for AQA GCSE Combined Science Required Practicals.
// 4th in the subject-hub family (sits alongside the Maths / English / Science hubs).
// Compelling for SEO because "required practicals" is a high-volume, low-competition
// keyword cluster ("aqa biology required practical 1", etc.) and we have authored content for all 13 RPs.

import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useState, useMemo } from 'react';
import {
  CheckCircle, Trophy, Lightning, Sparkle, ArrowRight,
  CaretDown, Microscope, Flask, Atom, ShieldCheck, Lightbulb,
} from '@phosphor-icons/react';

import SEO from '../components/SEO';
import { getAllRequiredPracticals } from '../data/requiredPracticals';

const SITE_URL = 'https://grade9ready.com';
const PATH = '/gcse-required-practicals';

const SUBJECT_META = {
  biology:   { Icon: Microscope,   color: 'emerald', label: 'Biology'   },
  chemistry: { Icon: Flask, color: 'amber',   label: 'Chemistry' },
  physics:   { Icon: Atom,         color: 'sky',     label: 'Physics'   },
};

const accent = {
  emerald: { chip: 'bg-emerald-100 text-emerald-700 border-emerald-200', bar: 'bg-emerald-500' },
  amber:   { chip: 'bg-amber-100 text-amber-700 border-amber-200',       bar: 'bg-amber-500'   },
  sky:     { chip: 'bg-sky-100 text-sky-700 border-sky-200',             bar: 'bg-sky-500'     },
};

const FAQS = [
  {
    q: 'What are the AQA GCSE Required Practicals?',
    a: 'The AQA GCSE Combined Science (Trilogy) specification mandates 13 specific Required Practicals — 5 in Biology, 4 in Chemistry and 4 in Physics. Around 15% of GCSE Science exam marks come from questions about these practicals. AQA Triple Science adds further practicals (Biology has 8, Chemistry 8, Physics 8) but the 13 in the Trilogy course are non-negotiable.',
  },
  {
    q: 'Do I need to memorise the methods?',
    a: 'You don\'t need to recall every step verbatim, but you DO need to be able to: describe the apparatus and method, identify variables (independent / dependent / control), spot sources of error, suggest improvements, plot and interpret data, and apply safety considerations. Every Grade9Ready RP topic walks through all of this with an AQA-style 6-mark exam question and AI marking.',
  },
  {
    q: 'Which practicals come up most often in exams?',
    a: 'AQA rotates them, but high-frequency favourites include: Biology RP1 (microscopy), RP3 (food tests) and RP5 (rate of photosynthesis); Chemistry RP1 (preparation of a salt) and RP3 (titrations); Physics RP1 (specific heat capacity), RP4 (resistance) and RP6 (waves on a string).',
  },
  {
    q: 'Can I do these practicals at home?',
    a: 'A few can be safely demonstrated at home (e.g. testing food groups, plant transpiration). For most, the safer route is to revise the methods, results and analysis using our walkthroughs — that\'s exactly what AQA tests in the written exam. Each Grade9Ready RP topic includes a method, results, sample data and the AQA mark scheme.',
  },
  {
    q: 'How do you test my understanding?',
    a: 'Every Required Practical has a 6-mark AQA-style exam question with AI marking — type your answer and our AI returns marks awarded, target-grade band, missed mark-scheme points, error-type classification (calculation slip / misread / misconception / missing-AO3) and an examiner-style summary.',
  },
  {
    q: 'Is this for Combined Science or Triple Science?',
    a: 'The 13 Required Practicals listed here are the Combined Science (Trilogy) set — a hard requirement for Combined students and a baseline for Triple students. Triple-only RPs (e.g. Chromatography of inks, Investigating force and acceleration) are flagged with a Higher / Triple badge inside each topic page.',
  },
];

function FAQItem({ q, a, idx }) {
  const [open, setOpen] = useState(idx === 0);
  return (
    <div className="neo-card p-0 overflow-hidden" data-testid={`rp-faq-item-${idx}`}>
      <button onClick={() => setOpen((v) => !v)} className="w-full flex items-center justify-between gap-3 p-5 text-left">
        <span className="font-heading font-bold text-base">{q}</span>
        <CaretDown size={18} weight="bold" className={`shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="px-5 pb-5 text-sm text-muted-foreground leading-relaxed border-t border-foreground/10 pt-4">
          {a}
        </div>
      )}
    </div>
  );
}

function RpCard({ item, subjectKey }) {
  const meta = SUBJECT_META[subjectKey];
  const cls = accent[meta.color];
  const Icon = meta.Icon;
  const code = item.rp?.code || '';
  const title = item.rp?.title || item.topicTitle;
  const summary = item.rp?.summary || item.rp?.aim || '';
  const tags = item.rp?.tags || [];
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className="neo-card p-5"
      data-testid={`rp-card-${code || item.topicSlug}`}
    >
      <div className="flex items-start justify-between gap-3 mb-2">
        <span className={`text-[10px] font-black uppercase tracking-wide px-2 py-1 rounded border ${cls.chip} flex items-center gap-1`}>
          <Icon size={11} weight="fill" /> {meta.label}{code ? ` ${code}` : ''}
        </span>
        <Lightning size={14} weight="fill" className="text-amber-500" />
      </div>
      <h3 className="font-heading font-bold text-base mb-1.5 leading-tight">{title}</h3>
      {summary && (
        <p className="text-xs text-muted-foreground leading-relaxed mb-3 line-clamp-3">{summary}</p>
      )}
      {tags.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {tags.slice(0, 3).map((t) => (
            <span key={t} className="text-[10px] font-bold px-2 py-0.5 rounded border border-foreground/10 bg-secondary/40 text-muted-foreground">{t}</span>
          ))}
        </div>
      )}
    </motion.div>
  );
}

export default function GCSERequiredPracticalsHub() {
  const all = useMemo(() => getAllRequiredPracticals(), []);
  const groups = useMemo(() => ([
    { key: 'biology',   label: 'Biology Required Practicals',   list: all.filter((r) => r.disciplineSlug === 'biology')   },
    { key: 'chemistry', label: 'Chemistry Required Practicals', list: all.filter((r) => r.disciplineSlug === 'chemistry') },
    { key: 'physics',   label: 'Physics Required Practicals',   list: all.filter((r) => r.disciplineSlug === 'physics')   },
  ]), [all]);

  const totalRPs = all.length;

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home',    item: `${SITE_URL}/` },
      { '@type': 'ListItem', position: 2, name: 'Subjects', item: `${SITE_URL}/subjects` },
      { '@type': 'ListItem', position: 3, name: 'GCSE Required Practicals', item: `${SITE_URL}${PATH}` },
    ],
  };
  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map((f) => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } })),
  };

  return (
    <div className="min-h-screen bg-background" data-testid="gcse-hub-required-practicals">
      <SEO
        title="GCSE Required Practicals — AQA Biology, Chemistry, Physics"
        description="Master the 13 AQA GCSE Required Practicals. Method, results, mark schemes and AI-marked 6-mark exam questions for every Biology, Chemistry and Physics RP."
        path={PATH}
      />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbJsonLd) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }} />

      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 text-xs text-muted-foreground" aria-label="Breadcrumb">
        <Link to="/" className="hover:text-foreground">Home</Link>
        <span className="mx-2">/</span>
        <Link to="/subjects" className="hover:text-foreground">Subjects</Link>
        <span className="mx-2">/</span>
        <span className="text-foreground font-bold">GCSE Required Practicals</span>
      </nav>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-12">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-fuchsia-100 text-fuchsia-700 border border-fuchsia-200 text-[11px] font-black uppercase tracking-wide mb-5">
          <Lightning size={12} weight="fill" /> AQA · Combined Science Trilogy
        </div>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-black leading-[1.05] mb-5 max-w-4xl" data-testid="rp-hub-h1">
          GCSE Required Practicals — every method, every mark scheme
        </h1>
        <p className="text-lg text-muted-foreground leading-relaxed mb-7 max-w-3xl">
          Around <strong>15% of your GCSE Science marks</strong> come from Required Practicals. Grade9Ready breaks down all <strong>{totalRPs} AQA Combined Science RPs</strong> with method, apparatus, sample data, sources of error, AQA-style 6-mark questions and instant AI marking.
        </p>
        <div className="flex flex-wrap gap-3 mb-2">
          <Link to="/signup" data-testid="rp-cta-trial" className="inline-flex items-center gap-2 px-6 py-3 rounded-2xl bg-primary text-primary-foreground font-heading font-bold shadow-[3px_3px_0px_0px_rgb(var(--9ready-shadow))] hover:translate-y-[1px] transition-transform">
            Create Parent Account <ArrowRight size={18} weight="bold" />
          </Link>
          <Link to="/science/required-practicals" data-testid="rp-cta-explore" className="inline-flex items-center gap-2 px-6 py-3 rounded-2xl border-2 border-foreground/15 font-heading font-bold hover:border-foreground/40 transition-colors">
            <Lightbulb size={16} weight="bold" /> Open the RP Hub
          </Link>
        </div>
        <p className="text-xs text-muted-foreground mt-3">No card required · cancel anytime · AQA spec 8464 / 8461 / 8462 / 8463</p>
      </section>

      {/* RP listings */}
      {groups.map((g) => (
        <section
          key={g.key}
          className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 border-t-2 border-foreground/10"
          data-testid={`rp-section-${g.key}`}
        >
          <div className="flex items-center gap-3 mb-6">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center border ${accent[SUBJECT_META[g.key].color].chip}`}>
              {(() => { const Icon = SUBJECT_META[g.key].Icon; return <Icon size={18} weight="bold" />; })()}
            </div>
            <h2 className="text-2xl sm:text-3xl font-heading font-black">{g.label}</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {g.list.map((item) => <RpCard key={item.rp?.code || item.topicSlug} item={item} subjectKey={g.key} />)}
          </div>
        </section>
      ))}

      {/* What you get */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 border-t-2 border-foreground/10">
        <h2 className="text-3xl sm:text-4xl font-heading font-black mb-8">What every RP topic includes</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { icon: Microscope,   title: 'Method walk-through',  body: 'Step-by-step apparatus, procedure and safety notes — exactly what AQA expects you to recall.' },
            { icon: ShieldCheck,  title: 'Sample data + analysis', body: 'Worked example tables, graphs, calculations, sources of error and improvement suggestions.' },
            { icon: Trophy,       title: '6-mark exam question',  body: 'AQA-style long-answer practice with AI marking and a target-grade band.' },
            { icon: Sparkle,      title: 'Examiner brain',        body: 'Why marks are lost, what examiners want, common traps and the Grade-9 move per practical.' },
          ].map((p, i) => {
            const Icon = p.icon;
            return (
              <div key={p.title} className="neo-card p-5" data-testid={`rp-feature-${i}`}>
                <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-primary/10 text-primary mb-3">
                  <Icon size={18} weight="bold" />
                </div>
                <h3 className="font-heading font-bold text-base mb-1.5">{p.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{p.body}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-14 border-t-2 border-foreground/10">
        <h2 className="text-3xl sm:text-4xl font-heading font-black mb-8 text-center">Frequently asked</h2>
        <div className="space-y-3" data-testid="rp-hub-faq">
          {FAQS.map((f, i) => <FAQItem key={f.q} q={f.q} a={f.a} idx={i} />)}
        </div>
      </section>

      {/* Final CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="neo-card p-8 sm:p-12 text-center bg-gradient-to-br from-primary/5 to-primary/10 border-primary/30">
          <CheckCircle size={28} weight="fill" className="mx-auto mb-3 text-primary" />
          <h2 className="text-3xl sm:text-4xl font-heading font-black mb-3">Lock in the 15% of marks others ignore</h2>
          <p className="text-muted-foreground max-w-xl mx-auto mb-6">
            Every Required Practical, every method, every mark scheme. Start your free 7-Day Early Access — no card required.
          </p>
          <Link to="/signup" data-testid="rp-cta-final" className="inline-flex items-center gap-2 px-6 py-3 rounded-2xl bg-primary text-primary-foreground font-heading font-bold shadow-[3px_3px_0px_0px_rgb(var(--9ready-shadow))] hover:translate-y-[1px] transition-transform">
            Create Parent Account <ArrowRight size={18} weight="bold" />
          </Link>
        </div>
      </section>
    </div>
  );
}
